import { BulkTogglesConfig } from 'ui-components'
import { Draft } from 'immer'
import { OrderOptionsConfig } from 'ui-components/column-settings/types'
import getReorder from 'ui-components/column-settings/get-reorder'

import {
  TaskColumnId,
  TaskColumnEntry,
  TaskColumnSettings,
  TaskBulkToggleKey,
  COLUMN_ORDER_TASKS,
  TASK_COLUMN_SPECS,
  TaskOrderOptionKey,
} from '../task-columns'

function makeTaskBulkTogglesConfig({
  defaultColumnIds,
}: {
  defaultColumnIds: TaskColumnId[]
}): BulkTogglesConfig<TaskColumnId, TaskColumnEntry, TaskBulkToggleKey> {
  const configs: BulkTogglesConfig<
    TaskColumnId,
    TaskColumnEntry,
    TaskBulkToggleKey
  > = {
    showingAll: {
      getEntryDesiredEnabled: () => true,
      label: 'Show All',
      selectedLabel: 'Showing All',
    },
    hidingAllExceptTaskIdAndConfigName: {
      getEntryDesiredEnabled: ({ id }: TaskColumnEntry) =>
        id === 'id' || id === 'companyName',
      label: 'Hide All Except Id & Company Name',
      selectedLabel: 'Hiding All Except Id & Company Name',
    },
    showingDefault: {
      getEntryDesiredEnabled: ({
        id,
      }: TaskColumnEntry | Draft<TaskColumnEntry>) =>
        defaultColumnIds.includes(id),
      label: 'Show Default',
      selectedLabel: 'Showing Default',
    },
  }
  return configs
}

function makeTaskOrderOptionsConfig(): OrderOptionsConfig<
  TaskColumnId,
  TaskOrderOptionKey
> {
  return {
    standard: {
      reorder: (enabledColumnIds: TaskColumnId[]) =>
        COLUMN_ORDER_TASKS.filter((columnId) =>
          enabledColumnIds.includes(columnId),
        ),
      label: 'Use Standard Ordering',
      selectedLabel: 'Using Standard Ordering',
    },
  }
}

const makeTaskColumnSettings = (
  defaultColumnIds: TaskColumnId[],
  alreadyEnabledColumnIds: TaskColumnId[],
  currentOrderOption: TaskOrderOptionKey = 'standard',
): TaskColumnSettings => {
  const orderOptionsConfig = makeTaskOrderOptionsConfig()
  const reorder = getReorder(orderOptionsConfig, currentOrderOption)
  const orderedDefaultColumns = reorder(defaultColumnIds)
  const enabledColumnIds = reorder(
    alreadyEnabledColumnIds.length
      ? alreadyEnabledColumnIds
      : orderedDefaultColumns,
  )
  if (!enabledColumnIds.includes('taskType')) {
    // this new column is mandatory and should be inserted at the front
    enabledColumnIds.splice(0, 0, 'taskType')
  }
  const columns = COLUMN_ORDER_TASKS.map((id) => ({
    id,
    label: TASK_COLUMN_SPECS[id].heading,
    heading: TASK_COLUMN_SPECS[id].heading,
    sortKey: TASK_COLUMN_SPECS[id].sortValueKey,
    minWidthPx: TASK_COLUMN_SPECS[id].minWidthPx ?? 0,
  }))
  const bulkTogglesConfig = makeTaskBulkTogglesConfig({
    defaultColumnIds: orderedDefaultColumns,
  })

  return {
    enabledColumnIds,
    columns,
    bulkTogglesConfig,
    currentOrderOption,
    orderOptionsConfig,
  }
}

export default makeTaskColumnSettings
